import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/user/Layout';
import WaterTrackingDashboard from '../components/WaterTrackingDashboard';
import WaterLogModal from '../components/user/WaterLogModal';
import PartnerOffers from '../components/PartnerOffers';
import { supabase } from '../lib/supabaseClient';
import { toast } from 'react-toastify';
import { 
  Droplets, 
  Trophy, 
  Target, 
  TrendingUp, 
  Award,
  Zap,
  Clock,
  Plus,
  Activity
} from 'lucide-react';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [waterAmount, setWaterAmount] = useState(0);
  const [dailyGoal, setDailyGoal] = useState(2500);
  const [points, setPoints] = useState(0);
  const [streak, setStreak] = useState(0);
  const [totalWaterConsumed, setTotalWaterConsumed] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dailyLimit] = useState(2500);
  
  // Nouvelles données pour l'interface améliorée
  const [weeklyProgress, setWeeklyProgress] = useState([]);
  const [todayHydrationRate, setTodayHydrationRate] = useState(0);

  useEffect(() => {
    if (user) {
      fetchUserData();
      fetchTodayWaterLogs();
      fetchWeeklyProgress();
    }
  }, [user]);

  // Calculs pour l'interface améliorée
  useEffect(() => {
    setTodayHydrationRate((waterAmount / dailyGoal) * 100);
  }, [waterAmount, dailyGoal]);

  // Refetch weekly progress when language changes
  useEffect(() => {
    if (user) {
      fetchWeeklyProgress();
    }
  }, [t, user, dailyGoal]);

  const fetchUserData = async () => {
    try {
      const { data: settingsData, error: settingsError } = await supabase
        .from('user_settings')
        .select('daily_goal, streak')
        .eq('user_id', user?.id)
        .single();
      
      if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;
      
      if (settingsData) {
        setDailyGoal(settingsData.daily_goal);
        setStreak(settingsData.streak || 0);
      }
      
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('total_points, water_volume_consumed')
        .eq('id', user?.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (profileData) {
        setPoints(profileData.total_points || 0);
        setTotalWaterConsumed(profileData.water_volume_consumed || 0);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error(t('error'));
    }
  };

  const fetchTodayWaterLogs = async () => {
    try {
      setLoading(true);
      
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0).toISOString();
      const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).toISOString();
      
      console.log('Fetching water logs between:', todayStart, 'and', todayEnd);
      
      const { data, error } = await supabase
        .from('water_logs')
        .select('amount, created_at')
        .eq('user_id', user?.id)
        .gte('created_at', todayStart)
        .lte('created_at', todayEnd);
      
      if (error) throw error;
      
      if (data) {
        console.log('Water logs for today:', data);
        const totalAmount = data.reduce((sum, log) => sum + log.amount, 0);
        setWaterAmount(totalAmount);
      } else {
        setWaterAmount(0);
      }
    } catch (error) {
      console.error('Error fetching water logs:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const getDayName = (date) => {
    const dayIndex = date.getDay();
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return t(dayKeys[dayIndex]);
  };

  const fetchWeeklyProgress = async () => {
    try {
      const weekData = [];
      
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0).toISOString();
        const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59).toISOString();
        
        const { data } = await supabase
          .from('water_logs')
          .select('amount')
          .eq('user_id', user?.id)
          .gte('created_at', dayStart)
          .lte('created_at', dayEnd);
        
        const total = data?.reduce((sum, log) => sum + log.amount, 0) || 0;
        weekData.push({
          date: getDayName(date),
          amount: total,
          percentage: (total / dailyGoal) * 100
        });
      }
      setWeeklyProgress(weekData);
    } catch (error) {
      console.error('Error fetching weekly progress:', error);
    }
  };

  const handleLogSuccess = () => {
    fetchTodayWaterLogs();
    fetchUserData();
    fetchWeeklyProgress();
  };

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <Layout title={t('welcomeBack') + ', ' + (user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User')}>
      <div className="max-w-sm sm:max-w-2xl lg:max-w-6xl mx-auto px-4 space-y-4">
        
        {/* Header avec statistiques rapides - Optimisé mobile */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-3 sm:p-4 text-white shadow-lg transform hover:scale-105 transition-transform duration-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1">
                <p className="text-blue-100 text-xs font-bold uppercase tracking-wide mb-1">
                  {Math.round(todayHydrationRate)}%
                </p>
                <p className="text-xl sm:text-lg font-black">{waterAmount}<span className="text-sm">ml</span></p>
              </div>
              <Droplets className="w-6 h-6 sm:w-8 sm:h-8 text-blue-200 mt-1 sm:mt-0" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-3 sm:p-4 text-white shadow-lg transform hover:scale-105 transition-transform duration-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1">
                <p className="text-green-100 text-xs font-bold uppercase tracking-wide mb-1">{t('streak')}</p>
                <p className="text-xl sm:text-lg font-black">{streak}<span className="text-sm">{t('days')}</span></p>
              </div>
              <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-green-200 mt-1 sm:mt-0" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-3 sm:p-4 text-white shadow-lg transform hover:scale-105 transition-transform duration-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1">
                <p className="text-purple-100 text-xs font-bold uppercase tracking-wide mb-1">{t('points')}</p>
                <p className="text-xl sm:text-lg font-black">{points}</p>
              </div>
              <Award className="w-6 h-6 sm:w-8 sm:h-8 text-purple-200 mt-1 sm:mt-0" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-3 sm:p-4 text-white shadow-lg transform hover:scale-105 transition-transform duration-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1">
                <p className="text-orange-100 text-xs font-bold uppercase tracking-wide mb-1">{t('time')}</p>
                <p className="text-xl sm:text-lg font-black">{getCurrentTime()}</p>
              </div>
              <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-orange-200 mt-1 sm:mt-0" />
            </div>
          </div>
        </div>

        {/* Section principale - Mobile First */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          
          {/* Panneau principal d'hydratation */}
          <div className="lg:col-span-2 bg-white rounded-3xl shadow-2xl p-4 sm:p-6 border border-gray-100 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-xl sm:text-2xl font-black text-gray-900 flex items-center">
                <Droplets className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 mr-2 sm:mr-3" />
                {t('hydrationTracking')}
              </h3>
              <div className="text-xs sm:text-sm text-gray-600 bg-gray-100 px-2 sm:px-3 py-1 rounded-full font-bold">
                {waterAmount}/{dailyGoal}ml
              </div>
            </div>
            
            <WaterTrackingDashboard
              dailyGoal={dailyGoal}
              waterAmount={waterAmount}
              streak={streak}
              points={points}
              totalWaterConsumed={totalWaterConsumed}
              onAddWater={() => setIsModalOpen(true)}
            />
            
            {waterAmount >= dailyLimit && (
              <div className="mt-4 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200 text-yellow-800 p-3 sm:p-4 rounded-2xl text-center shadow-lg">
                <div className="flex items-center justify-center">
                  <Target className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  <span className="font-bold text-sm sm:text-base">{t('limitReached')}</span>
                </div>
              </div>
            )}
          </div>

          {/* Panneau de progression hebdomadaire - Mobile optimisé */}
          <div className="bg-white rounded-3xl shadow-2xl p-4 sm:p-6 border border-gray-100 backdrop-blur-sm">
            <h4 className="text-lg sm:text-xl font-black text-gray-900 mb-4 sm:mb-6 flex items-center">
              <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2" />
              <span className="text-sm sm:text-base">{t('weeklyProgress')}</span>
            </h4>
            
            <div className="space-y-3 sm:space-y-4">
              {weeklyProgress.map((day, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm font-black text-gray-700 w-8 sm:w-12">
                    {day.date}
                  </span>
                  <div className="flex-1 mx-2 sm:mx-3">
                    <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3 shadow-inner">
                      <div 
                        className={`h-2 sm:h-3 rounded-full transition-all duration-700 shadow-sm ${
                          day.percentage >= 100 
                            ? 'bg-gradient-to-r from-green-400 to-green-500 shadow-green-200' 
                            : day.percentage >= 75
                            ? 'bg-gradient-to-r from-blue-400 to-blue-500 shadow-blue-200'
                            : day.percentage >= 50
                            ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 shadow-yellow-200'
                            : 'bg-gradient-to-r from-gray-400 to-gray-500'
                        }`}
                        style={{ width: `${Math.min(day.percentage, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-gray-600 w-8 sm:w-12 text-right">
                    {Math.round(day.percentage)}%
                  </span>
                </div>
              ))}
            </div>

            {/* Statistiques rapides - Mobile optimisé */}
            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-3 sm:gap-4 text-center">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-3 shadow-sm">
                  <p className="text-xl sm:text-2xl font-black text-blue-600">
                    {(totalWaterConsumed / 1000).toFixed(2)} L
                  </p>
                  <p className="text-xs text-blue-600 font-bold">{t('total')}</p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-3 shadow-sm">
                  <p className="text-xl sm:text-2xl font-black text-green-600">
                    {weeklyProgress.filter(day => day.percentage >= 100).length}
                  </p>
                  <p className="text-xs text-green-600 font-bold">{t('goals')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section des offres partenaires - Mobile First */}
        <div className="bg-white rounded-3xl shadow-2xl p-4 sm:p-6 border border-gray-100 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl font-black text-gray-900 flex items-center">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500 mr-2 sm:mr-3" />
              {t('partnerOffers')}
            </h3>
          </div>
          <PartnerOffers />
        </div>
      </div>
      
      <WaterLogModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleLogSuccess}
        dailyTotal={waterAmount}
        dailyLimit={dailyLimit}
      />
    </Layout>
  );
};

export default Dashboard;